import { Component, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Router, NavigationEnd } from "@angular/router";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

import { AppState } from "../../../root.reducer";
import { ConsultantSkillApiActions } from "@skills-app/consultant-skill/actions";
import { NavmenuActions } from "@skills-app/core/actions";

import { EasyAuthService, ApiUser } from "../../services/easyauth.service";

import {
  faMagnifyingGlass,
  faBookOpenReader,
  faUser,
  faGraduationCap,
  faUserPlus,
  faRoad,
  faCircleInfo,
  faFolderTree,
  faCog,
  faLocationDot,
  faWrench,
  faPencil,
  faBookOpen,
  faChartSimple,
  faMessage,
  faRightToBracket,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

@Component({
  standalone: false,
  selector: "app-nav-menu",
  templateUrl: "./navmenu.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./navmenu.component.scss"],
})
export class NavMenuComponent implements OnDestroy {
  // Role-derived flags (match route guards)
  isAdministrator = false; // SkillsAdministrator
  isTaxonomyAdministrator = false; // TaxonomyAdministrator
  isCapabilityAdministrator = false; // CapabilityAdministrator

  isAuthenticated = false;
  userDisplay = "";

  // FontAwesome icons
  faMagnifyingGlass = faMagnifyingGlass;
  faBookOpenReader = faBookOpenReader;
  faUser = faUser;
  faGraduationCap = faGraduationCap;
  faUserPlus = faUserPlus;

  faRoad = faRoad;
  faCog = faCog;

  faCircleInfo = faCircleInfo;
  faFolderTree = faFolderTree;
  faLocationDot = faLocationDot;
  faWrench = faWrench;
  faPencil = faPencil;
  faBookOpen = faBookOpen;
  faChartSimple = faChartSimple;

  // Alias so template can use faChartBar safely
  faChartBar = faChartSimple;

  faMessage = faMessage;

  faRightToBracket = faRightToBracket;
  faRightFromBracket = faRightFromBracket;

  private currentUser: ApiUser | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly easyAuth: EasyAuthService,
  ) {
    // Prime state once at construction (snapshot may be empty initially)
    this.refreshFromUser(this.currentUser);

    // Update whenever the /api/me-backed user changes
    this.easyAuth
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => this.refreshFromUser(u));

    // Keep existing behavior: refresh on navigation end
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.store.dispatch(NavmenuActions.endNavigation());
        this.refreshFromUser(this.currentUser);
        this.collapseNavbar();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshFromUser(user: ApiUser | null): void {
    this.currentUser = user;

    const isAuthed = user?.isAuthenticated === true;
    const roles = user?.roles ?? [];

    this.isAuthenticated = isAuthed;

    // Role flags aligned to /api/me roles
    this.isAdministrator = roles.includes("SkillsAdministrator");
    this.isTaxonomyAdministrator = roles.includes("TaxonomyAdministrator");
    this.isCapabilityAdministrator = roles.includes("CapabilityAdministrator");

    // Optional display (name/email)
    this.userDisplay = (user?.name || user?.email || "") ?? "";
  }

  private isMobileViewport(): boolean {
    return typeof window !== "undefined" && window.innerWidth < 992;
  }

  public collapseNavbar(): void {
    if (!this.isMobileViewport()) return;

    const collapseEl = document.getElementById("navbarNavDropdown");
    if (collapseEl?.classList.contains("show")) {
      collapseEl.classList.remove("show");
    }

    const toggler =
      document.querySelector<HTMLButtonElement>(".navbar-toggler");
    if (toggler) {
      toggler.setAttribute("aria-expanded", "false");
      toggler.classList.add("collapsed");
    }

    document
      .querySelectorAll<HTMLElement>(".dropdown-menu.show")
      .forEach((menu) => menu.classList.remove("show"));

    document
      .querySelectorAll<HTMLElement>(".nav-link.dropdown-toggle.show")
      .forEach((toggle) => {
        toggle.classList.remove("show");
        toggle.setAttribute("aria-expanded", "false");
      });
  }

  public onNavLinkClick(): void {
    this.collapseNavbar();
  }

  login(): void {
    this.easyAuth.login();
  }

  logout(): void {
    this.easyAuth.logout("/");
  }

  triggerSync(): void {
    this.store.dispatch(ConsultantSkillApiActions.triggerAzureSync());
  }

  doubleClickNavigate(routePath: string): void {
    this.router.navigate([routePath]);
  }
}
